import { RelatorioComposicaoReceitasHorizontalItem } from "features/relatorios/typings";
import { formatMoney } from "infra/helpers/number";
import { useMemo } from "react";
import { PDFTableColumn } from "components/PDF/Table";
import { View, Text } from "@react-pdf/renderer";
import moment from "moment";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";

function useRelatorioComposicaoReceitasHorizontalPdfColumns({
  todasAsCategorias,
  groupByFields,
}: {
  todasAsCategorias?: { id: string; nome: string; breadcrumb: string }[];
  groupByFields?: string[];
}): PDFTableColumn<RelatorioComposicaoReceitasHorizontalItem>[] {
  return useMemo(() => {
    const columns: PDFTableColumn<RelatorioComposicaoReceitasHorizontalItem>[] =
      [];

    columns.push({
      title: "Unidade",
      flexGrow: 1,
      render: (original) => (
        <View>
          <Text>
            {formatBlocoQuadra(original?.unidade, original?.blocoQuadra, "")}
          </Text>
        </View>
      ),
    });

    columns.push({
      title: "Nome",
      flexGrow: 1,
      render: (original) => (
        <View>
          <Text>{original?.pessoa ?? "--"}</Text>
        </View>
      ),
    });

    columns.push({
      title: "Competência",
      flexGrow: 0.9,
      textAlign: "center",
      render: (original) => (
        <View>
          <Text>
            {original.dataCompetencia &&
            moment(original.dataCompetencia, "YYYY-MM").isValid()
              ? moment(original.dataCompetencia, "YYYY-MM").format("MM/YYYY")
              : "--"}
          </Text>
        </View>
      ),
    });

    columns.push({
      title: "Vencimento",
      flexGrow: 0.8,
      textAlign: "center",
      render: (original) => (
        <View>
          <Text>
            {original.dataVencimento
              ? moment(original.dataVencimento).format("DD/MM/YYYY")
              : "--"}
          </Text>
        </View>
      ),
    });

    columns.push({
      title: "Liquidação",
      flexGrow: 0.7,
      textAlign: "center",
      render: (original) => (
        <View>
          <Text>
            {original.dataPagamento
              ? moment(original.dataPagamento).format("DD/MM/YYYY")
              : "--"}
          </Text>
        </View>
      ),
    });

    columns.push({
      title: "Crédito",
      flexGrow: 0.7,
      textAlign: "center",
      render: (original) => (
        <View>
          <Text>
            {original.dataMovimentacao
              ? moment(original.dataMovimentacao).format("DD/MM/YYYY")
              : "--"}
          </Text>
        </View>
      ),
    });

    columns.push({
      title: "Nosso Número",
      flexGrow: 0.8,
      textAlign: "left",
      render: (original) => (
        <View>
          <Text>{original.nossoNumero ?? "--"}</Text>
        </View>
      ),
    });

    columns.push({
      title: groupByFields?.length ? "Sub-Total" : "Total",
      textAlign: "right",
      render: (original: RelatorioComposicaoReceitasHorizontalItem) => {
        const categoriasASomar = Object.entries(original.composicoes);

        const soma = categoriasASomar.reduce(
          (acc, [_, valor]) => (acc += valor),
          0
        );

        return (
          <View>
            <Text style={{ textAlign: "right" }}>{formatMoney(soma ?? 0)}</Text>
          </View>
        );
      },
    });

    if (todasAsCategorias?.length) {
      const primeiras4Categorias = todasAsCategorias.slice(0, 4);

      primeiras4Categorias.forEach((categoria) =>
        columns.push({
          title: `${categoria.nome}`,
          flexGrow: 1,
          textAlign: "right",
          render: (original) => (
            <View>
              <Text>
                {formatMoney(original.composicoes[categoria.id] ?? 0)}
              </Text>
            </View>
          ),
        })
      );

      if (todasAsCategorias.length > 4) {
        columns.push({
          title: "Outras",
          textAlign: "right",
          render: (original: RelatorioComposicaoReceitasHorizontalItem) => {
            const categoriasASomar = Object.entries(
              original.composicoes
            ).filter(
              ([key]) =>
                !primeiras4Categorias.find((categoria) => categoria.id === key)
            );

            const soma = categoriasASomar.reduce(
              (acc, [_, valor]) => (acc += valor),
              0
            );

            return (
              <View>
                <Text style={{ textAlign: "right" }}>
                  {formatMoney(soma ?? 0)}
                </Text>
              </View>
            );
          },
        });
      }
    }

    return columns;
  }, [groupByFields?.length, todasAsCategorias]);
}

export default useRelatorioComposicaoReceitasHorizontalPdfColumns;
